import {
    type AgebandPricingData,
    type Currency,
    type FlatPricingData,
    type GetProductsResItem0ResolvedSchedulesItemDto,
    type GetProductsResItem0ScheduleItemDto,
    ProductOptionType,
    ProductType,
    TransportationMedium,
} from '../../generated';
import {
    Activity,
    AgebandName,
    DurationValue,
    PricingType,
    DiscountType,
    TransactionMethod,
    SourceType,
} from '../common.types';
import { UserGroupType } from '../team/team.types';
import { Attachment } from '../customer/customer.types';
import { ScheduleOptionTypes } from '../schedule/schedule.types';

// re-exporting enums
export { ProductType, ProductOptionType, TransportationMedium };

export interface PricingSource {
    sourceType: SourceType;
    sourceName?: string;
    referral?: {
        id?: string;
    };
}

export interface Pricing {
    id: string;
    currency: string;
    name: string;
    type: PricingType;
    capacity?: Omit<ProductCapacityItem, 'id'>;
    schedule?: Omit<ProductScheduleItem, 'id'>;
    source?: PricingSource;
    price: {
        base?: number;
        perProduct?: number;
        perPerson?: number;
        counts?: Partial<Record<AgebandName, number>>;
    };
    cost?: {
        base?: number;
        perProduct?: number;
        perPerson?: number;
        counts?: Partial<Record<AgebandName, number>>;
    };
    inclusions: { value: string }[];
    exclusions: { value: string }[];
}

export interface Option {
    id: string;
    uuid?: string;
    name: string;
    optionType: ProductOptionType;
    description: string;
    hasPricing?: boolean;
    prices: Pricing[];
}

export interface ProductInfo {
    name: string;
    description: string;
    languages: string[];
    productCategories: string[];
    interests: string[];
    accessibility: string[];
}

export interface RouteItem {
    name: string;
    repeatLocation?: boolean;
    arrivalOffset: DurationValue;
    duration: DurationValue;
    id?: string;
    isEdit?: boolean;
    isNew?: boolean;
}

export enum DistanceType {
    KM = 'km',
    MILE = 'mi',
}

export interface RouteDistance {
    value: number;
    type: DistanceType;
}

export interface ProductRoute {
    stops: RouteItem[];
    distance: RouteDistance;
}

export interface ProductScheduleItem {
    id: string;
    uuid?: string;
    type?: ScheduleOptionTypes;
    scheduleId: string;
    name: string;
}

export interface ProductCapacityItem {
    id: string;
    uuid?: string;
    capacityId: string;
    name: string;
}

export interface ProductFinancial {
    currency: string;
    items: Pricing[];
    adaptivePricing: boolean;
    paymentMethods: TransactionMethod[];
    prepayment?: boolean;
    prepaymentAmount?: number;
    prepaymentType?: DiscountType;
}

export interface AddProductForm {
    type: ProductType.TOUR | ProductType.TRANSFER;
    configuration: {
        autoscaled: boolean;
        shared: boolean;
        transportationMedium?: string;
        singleUse?: boolean;
        sell_via_widget: boolean | null;
    };
    productInfo: ProductInfo;
    route: ProductRoute;
    schedule: {
        duration?: DurationValue;
        swing?: string;
        items: ProductScheduleItem[];
    };
    capacity: {
        capacities: ProductCapacityItem[];
    };
    financial?: ProductFinancial;
    options: {
        items: Option[];
    };
}

export interface Product extends AddProductForm {
    id: string;
    created: any;
    deleted?: any;
    active: boolean;
    lifecycle: Activity[];
    permissions: {
        canEdit: boolean;
        canDelete: boolean;
        canShare: boolean;
    };
    sharedTeams: {
        id: string;
        type: UserGroupType;
    }[];
    policies: {
        id: string;
        content: string;
    }[];
    attachments: Attachment[];
}

export interface PackageProductItem {
    id: string;
    bufferMinutes: number;
    orderIndex: number;
    ref: { type: ProductType; id: string; name: string };
    startTime: {
        days: number;
        hours: number;
        minutes: number;
        seconds: number;
    };
}

export interface PackageDayItem {
    offsetDays: number;
    products: PackageProductItem[];
}

export interface Package {
    id: string;
    created: any;
    deleted?: any;
    active: boolean;
    lifecycle: Activity[];
    type: ProductType.DYNAMIC_PACKAGE;
    productInfo: ProductInfo;
    days: PackageDayItem[];
    options: {
        items: Option[];
    };
    permissions: {
        canEdit: boolean;
        canDelete: boolean;
        canShare: boolean;
    };
    sharedTeams: {
        id: string;
        type: UserGroupType;
    }[];
    policies: {
        id: string;
        content: string;
    }[];
    attachments: Attachment[];
}

export interface ProductMinimal {
    id: string;
    name: string;
    type: ProductType;
    shared?: boolean;
    currency?: Currency;
    price?: FlatPricingData | AgebandPricingData | number;
    isAutoscale: boolean;
    schedule: GetProductsResItem0ScheduleItemDto[];
    resolvedSchedules?: GetProductsResItem0ResolvedSchedulesItemDto[];
}
